// @ts-nocheck
import { useMemo, useState } from 'react';
import { Stack, Title, Text as MantineText, Group, Badge, Code, Divider } from '@mantine/core';
import { Card, Button } from '../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../components/advanced';
import { mockUsers, type MockUser } from '../mockData/users';

type UserRow = MockUser & { id: string };

const columns: DataTableColumn<UserRow>[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'role', label: 'Role', sortable: true },
  { key: 'department', label: 'Department', sortable: true },
  {
    key: 'status',
    label: 'Status',
    render: (value) => (
      <Badge size="sm" color={value === 'active' ? 'green' : value === 'busy' ? 'red' : 'yellow'}>
        {String(value || 'unknown').toUpperCase()}
      </Badge>
    ),
  },
  {
    key: 'joinedAt',
    label: 'Joined',
    sortable: true,
    render: (value) => new Date(value).toLocaleDateString(),
  },
];

const filters = [
  {
    key: 'department',
    label: 'Department',
    options: [
      { value: 'Engineering', label: 'Engineering' },
      { value: 'Design', label: 'Design' },
      { value: 'Product', label: 'Product' },
      { value: 'Marketing', label: 'Marketing' },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'away', label: 'Away' },
      { value: 'busy', label: 'Busy' },
      { value: 'offline', label: 'Offline' },
    ],
  },
];

export default function DataTablesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(columns.map((c) => String(c.key)));

  // Build a larger dataset for infinite scroll
  const extendedUsers = useMemo<UserRow[]>(() => {
    const repeated = Array.from({ length: 4 }).flatMap((_, idx) =>
      mockUsers.map((u, i) => ({
        ...u,
        id: `${u.id}-${idx}-${i}`,
      }))
    );
    return repeated;
  }, []);

  // Basic search & pagination
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return mockUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q) ||
        user.role.toLowerCase().includes(q) ||
        user.department.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const [scrollData, setScrollData] = useState<UserRow[]>(extendedUsers.slice(0, 15));
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setTimeout(() => {
      const next = extendedUsers.slice(0, scrollData.length + 10);
      setScrollData(next);
      setLoadingMore(false);
    }, 400);
  };

  const hasMore = scrollData.length < extendedUsers.length;

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Data Tables</Title>
        <MantineText c="dimmed" size="sm">
          Interactive tables with column toggles, search, filtering, responsive layout, pagination, and infinite scroll.
        </MantineText>
      </Stack>

      {/* Feature-rich table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Feature-rich Table</Title>
            <MantineText size="sm" c="dimmed">
              Sorting, filtering, search, selection, pagination, and column visibility controls.
            </MantineText>
          </Stack>

          <DataTable<UserRow>
            data={paged}
            columns={columns}
            selectable
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            onSearch={setSearchQuery}
            searchPlaceholder="Search users..."
            filters={filters}
            gradientVariant="primary"
            stickyHeader
            pagination={{
              page,
              pageSize,
              total: filtered.length,
              onPageChange: setPage,
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPage(1);
              },
            }}
            enableColumnVisibility
            defaultVisibleColumns={visibleColumns}
            onVisibleColumnsChange={setVisibleColumns}
            withRowAnimations
          />

          <Divider />
          <Code block language="tsx">
{`<DataTable
  data={rows}
  columns={columns}
  selectable
  onSelectionChange={setSelected}
  onSearch={setQuery}
  filters={filters}
  pagination={{ page, pageSize, total, onPageChange, onPageSizeChange }}
  enableColumnVisibility
  withRowAnimations
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Responsive & Column Chooser */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Responsive & Column Chooser</Title>
            <MantineText size="sm" c="dimmed">
              Collapse into stacked cards on small screens and toggle columns from the menu.
            </MantineText>
          </Stack>

          <DataTable<UserRow>
            data={mockUsers}
            columns={columns}
            responsiveBreakpoint="md"
            enableColumnVisibility
            defaultVisibleColumns={['name', 'email', 'role', 'status']}
            highlightOnHover
            striped
            searchPlaceholder="Search..."
            enableInternalSearch
            filters={filters}
            withRowAnimations
          />
        </Stack>
      </Card>

      {/* Infinite Scroll & Animated */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Infinite Scroll & Animated Rows</Title>
            <MantineText size="sm" c="dimmed">
              Load more data as you scroll while animating row entrance.
            </MantineText>
          </Stack>

          <DataTable<UserRow>
            data={scrollData}
            columns={columns}
            enableInternalSearch
            enableInternalSort
            withRowAnimations
            infiniteScroll={{
              enabled: true,
              loadMore,
              hasMore,
              loading: loadingMore,
            }}
            emptyMessage="No data yet"
          />

          <Group justify="flex-end">
            <Button onClick={loadMore} disabled={!hasMore} loading={loadingMore} variant="light">
              {hasMore ? 'Load more' : 'All data loaded'}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
